import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = categories.find((c) => c.slug === article?.category);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <main>
          <Navbar />
          <section className="pt-32 pb-20 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
              <Link to="/categories" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap">
                返回分类浏览
              </Link>
            </div>
          </section>
          <Footer />
        </main>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            articleSection: category?.name || '',
            publisher: {
              '@type': 'Organization',
              name: 'CoreNext 算力研究院',
              url: 'https://www.corenext-tech.com/',
            },
            url: `https://www.corenext-tech.com/articles/${article.slug}`,
          }),
        }}
      />
      <main>
        <Navbar />

        <article className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-3">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览', href: '/categories' },
                  { label: category?.name || '', href: `/categories?cat=${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mt-8 mb-6">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/categories?cat=${article.category}`}
                  className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap hover:bg-primary-100 transition-colors"
                >
                  {category?.name}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-secondary-100 text-secondary-600 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 leading-snug mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-400 pb-8 border-b border-background-200/70">
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span>{article.date}</span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </div>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div
              className="prose-custom text-sm leading-relaxed text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-5">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/articles/${ra.slug}`}
                      className="group p-4 bg-background-100 rounded-lg hover:bg-background-100/80 transition-colors"
                    >
                      <h4 className="font-heading font-medium text-sm text-foreground-900 mb-1.5 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        <Footer />
      </main>
    </>
  );
}